// EmployeeManagementPage.js
import React, { useState, useEffect, useRef } from 'react';
import { 
  FaDownload, 
  FaUsers, 
  FaChevronLeft, 
  FaChevronRight, 
  FaBook, 
  FaSearch,
  FaFilter,
  FaTimes,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaBuilding
} from 'react-icons/fa';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import styles from './EducationReportsPage.module.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import TableFilter from '../../components/TableFilter';
import { useTheme } from '../../contexts/ThemeContext';
import { useUserProfile } from '../../contexts/UserProfileContext';
import './EducationReportsDarkMode.css'; // Import dark mode styles

const truncateText = (text, maxLength = 15) => {
  if (
    text === null || 
    text === undefined || 
    text === '' || 
    Number.isNaN(text) ||
    (typeof text === 'string' && 
      ['nan', 'nat', 'n/a', 'null', 'undefined'].includes(text.toLowerCase()))
  ) {
    return '-';
  }
  const textStr = String(text);
  return textStr.length > maxLength 
    ? `${textStr.substring(0, maxLength)}...` 
    : textStr;
};

const EducationManagementPage = () => {
  const { isDarkMode } = useTheme();
  const { employeeId, employeeData } = useUserProfile(); // Get current SMO user info
  const [employees, setEmployees] = useState([]);
  const [sops, setSOPs] = useState([]);
  const [centers, setCenters] = useState([]); // Add centers state
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [filters, setFilters] = useState({});
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(true);

  const tableContainerRef = useRef(null);
  const filtersRef = useRef(null);

  const columns = {
    eName: { label: 'Ad', type: 'text' },
    eSurname: { label: 'Soyad', type: 'text' },
    project: { label: 'Proje', type: 'text' },
    departman: { label: 'Departman', type: 'text' },
    sopStatus: { 
      label: 'Eğitim Durumu', 
      type: 'select',
      options: [
        { value: 'Başarılı', label: 'Başarılı' },
        { value: 'Başarısız', label: 'Başarısız' },
        { value: 'Devam Ediyor', label: 'Devam Ediyor' }
      ]
    }
  };

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      
      // Fetch centers where current user is SMO
      const centersRef = collection(db, 'Centers');
      const centersQuery = query(centersRef, where('SMO', '==', employeeId));
      const centersSnapshot = await getDocs(centersQuery);
      const centersData = centersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCenters(centersData);

      // Fetch all employees
      const employeesSnapshot = await getDocs(collection(db, 'Employees'));
      const employeesData = employeesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Fetch all SOPs
      const sopsSnapshot = await getDocs(collection(db, 'SOPs'));
      const sopsData = sopsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSOPs(sopsData);

      // Filter employees to only those in centers managed by current SMO
      const filteredEmployees = employeesData.filter(emp => {
        // Check if employee is assigned to any project in SMO's centers
        return emp.projects?.some(project => 
          centersData.some(center => center.id === project.centerId)
        );
      });

      setEmployees(filteredEmployees);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Veri yüklenirken hata oluştu');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (employeeId) {
      fetchEmployees();
    }

    // Add click event listener to close filters dropdown when clicking outside
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [employeeId]);

  const handleClickOutside = (event) => {
    if (filtersRef.current && !filtersRef.current.contains(event.target)) {
      setShowFilters(false);
    }
  };

  useEffect(() => {
    let filtered = employees;

    // Apply search term first (searches name, surname, and department)
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(
        emp => 
          (emp.eName && emp.eName.toString().toLowerCase().includes(lowercasedSearch)) ||
          (emp.eSurname && emp.eSurname.toString().toLowerCase().includes(lowercasedSearch)) ||
          (emp.departman && emp.departman.toString().toLowerCase().includes(lowercasedSearch)) ||
          (emp.projects && emp.projects.some(project => 
            project.titleShort && project.titleShort.toString().toLowerCase().includes(lowercasedSearch)
          ))
      );
    }

    // Then apply column filters
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        if (key === 'sopStatus') {
          filtered = filtered.filter((emp, index) => {
            const status = getStatus(index);
            return status.text === filters[key];
          });
        } else {
          filtered = filtered.filter(item =>
            String(item[key])
              ?.toLowerCase()
              .includes(filters[key].toLowerCase())
          );
        }
      }
    });

    // Filter out employees with no required SOPs
    filtered = filtered.filter(emp => {
      const requiredSOPs = getRequiredSOPs(emp.departman);
      return requiredSOPs.length > 0;
    });

    setFilteredEmployees(filtered);
  }, [employees, sops, filters, searchTerm]);

  const getRequiredSOPs = (employeeDepartment) => {
    return sops.filter(sop => sop.group === employeeDepartment);
  };

  const handleFilter = (column, value) => {
    const newFilters = { ...filters, [column]: value };
    setFilters(newFilters);
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
  };

  const getStatus = (index) => {
    // Every third employee will have a different status
    const position = index % 3;
    const statuses = [
      { text: 'Başarılı', backgroundColor: '#38A169', color: '#fff', icon: <FaCheckCircle /> },
      { text: 'Başarısız', backgroundColor: '#E53E3E', color: '#fff', icon: <FaTimesCircle /> },
      { text: 'Devam Ediyor', backgroundColor: '#DD6B20', color: '#fff', icon: <FaClock /> }
    ];
    return statuses[position];
  };

  const calculateStats = () => {
    const relevantEmployees = filteredEmployees.length > 0 ? filteredEmployees : employees.filter(emp => {
      const requiredSOPs = getRequiredSOPs(emp.departman);
      return requiredSOPs.length > 0;
    });

    let total = 0;
    let completed = 0;
    let pending = 0;

    relevantEmployees.forEach((emp, index) => {
      const requiredSOPs = getRequiredSOPs(emp.departman);
      const status = getStatus(index);

      total += requiredSOPs.length;
      
      if (status.text === 'Başarılı') {
        completed += requiredSOPs.length;
      } else {
        pending += requiredSOPs.length;
      }
    });

    return { total, completed, pending };
  };

  const handleEmployeeSelect = (emp) => {
    setSelectedEmployee(emp);
  };

  const handleScroll = () => {
    if (tableContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tableContainerRef.current;
      setShowLeftScroll(scrollLeft > 0);
      setShowRightScroll(scrollLeft < scrollWidth - clientWidth);
    }
  };

  const scrollTable = (direction) => {
    if (tableContainerRef.current) {
      const scrollAmount = 300;
      const newScrollLeft = tableContainerRef.current.scrollLeft + 
        (direction === 'right' ? scrollAmount : -scrollAmount);
      
      tableContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      handleScroll();
      
      // Prevent wheel events from propagating beyond the table container
      const preventWheelPropagation = (e) => {
        // Check if the table actually needs scrolling
        const { scrollHeight, clientHeight, scrollWidth, clientWidth } = container;
        const hasVerticalScroll = scrollHeight > clientHeight;
        const hasHorizontalScroll = scrollWidth > clientWidth;
        
        // Only handle the event if there's something to scroll
        if (hasVerticalScroll || hasHorizontalScroll) {
          // Always prevent the wheel event from affecting the page
          e.stopPropagation();
          e.preventDefault();
          
          // Manually handle the scroll within the table container
          const scrollAmount = e.deltaY || e.deltaX;
          if (e.shiftKey || (!hasVerticalScroll && hasHorizontalScroll)) {
            // Horizontal scroll if shift is pressed or only horizontal scrolling is available
            container.scrollLeft += scrollAmount;
          } else {
            // Vertical scroll
            container.scrollTop += scrollAmount;
          }
        }
      };
      
      container.addEventListener('scroll', handleScroll);
      container.addEventListener('wheel', preventWheelPropagation, { passive: false });
      
      return () => {
        container.removeEventListener('scroll', handleScroll);
        container.removeEventListener('wheel', preventWheelPropagation);
      };
    }
  }, [employees, filteredEmployees]);

  // Apply dark mode class to document body
  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    
    return () => {
      document.body.classList.remove('dark-mode');
    };
  }, [isDarkMode]);

  const downloadExcel = async () => {
    try {
      const ExcelJS = (await import('exceljs')).default;
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Education Reports');
      
      // Define columns
      worksheet.columns = [
        { header: 'Ad', key: 'eName', width: 15 },
        { header: 'Soyad', key: 'eSurname', width: 15 },
        { header: 'Departman', key: 'departman', width: 20 },
        { header: 'Gerekli SOP\'lar', key: 'requiredSOPs', width: 30 },
        { header: 'Durum', key: 'status', width: 15 }
      ];
      
      // Add data
      const relevantEmployees = employees.filter(emp => {
        const requiredSOPs = getRequiredSOPs(emp.departman);
        return requiredSOPs.length > 0;
      });
      
      relevantEmployees.forEach((emp, index) => {
        const requiredSOPs = getRequiredSOPs(emp.departman);
        const status = getStatus(index);
        
        worksheet.addRow({
          eName: emp.eName || '-',
          eSurname: emp.eSurname || '-',
          departman: emp.departman || '-',
          requiredSOPs: requiredSOPs.map(sop => sop.name).join(', '),
          status: status.text
        });
      });
      
      // Style the header row
      worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFE5E7EB' } };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF111827' }
        };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
      });
      
      // Generate and download the file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'education_reports.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel dosyası başarıyla indirildi');
    } catch (error) {
      console.error('Error downloading Excel file:', error);
      toast.error('Dosya indirilirken hata oluştu');
    }
  };

  const stats = calculateStats();

  return (
    <div className={`${styles.pageContainer} ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className={`${styles.container} ${isDarkMode ? 'dark-mode' : ''}`}>
        <header className={`${styles.header} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={styles.headerContent}>
            <div>
              <h1 className={styles.title}>
                <FaBook className={styles.headerIcon} />
                Eğitim Raporları
              </h1>
              <div className={styles.breadcrumb}>Raporlar / Eğitim Raporları</div>
            </div>
            <div className={styles.headerButtons}>
              <button className={styles.downloadButton} onClick={downloadExcel}>
                <FaDownload className={styles.buttonIcon} /> Tabloyu İndir
              </button>
            </div>
          </div>
        </header>

        {/* SMO Info */}
        <div className={styles.smoInfo} style={{ marginBottom: '1rem' }}>
          <span>SMO: {employeeData?.eName} {employeeData?.eSurname}</span>
        </div>

        {/* Stats Cards */}
        <div className={styles.cardContainer}>
          <div className={`${styles.card} ${styles.cardTotal}`}>
            <div className={styles.cardContent}>
              <div className={styles.cardTitle}>Toplam Atanmış Olduğunuz Merkez Sayısı</div>
              <div className={styles.cardValue}>{centers.length}</div>
            </div>
            <FaBuilding className={styles.cardIcon} />
          </div>
          
          {centers.length > 0 && (
            <div className={`${styles.card} ${styles.cardActive}`}>
              <div className={styles.cardContent}>
                <div className={styles.cardTitle}>Bu Merkezlerdeki Çalışan Sayısı</div>
                <div className={styles.cardValue}>{employees.length}</div>
              </div>
              <FaUsers className={styles.cardIcon} />
            </div>
          )}
          
          {centers.length > 0 && (
            <div className={`${styles.card} ${styles.cardInactive}`}>
              <div className={styles.cardContent}>
                <div className={styles.cardTitle}>Toplam SOP Sayısı</div>
                <div className={styles.cardValue}>{stats.total}</div>
              </div>
              <FaBook className={styles.cardIcon} />
            </div>
          )}
        </div>

        <div className={styles.searchFilterContainer}>
          <div className={styles.searchBar}>
            <FaSearch className={styles.searchIcon} />
            <input
              type="text"
              placeholder="İsim, soyisim, departman veya proje ile ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
            {searchTerm && (
              <button 
                className={styles.clearSearch} 
                onClick={() => setSearchTerm('')}
              >
                <FaTimes />
              </button>
            )}
          </div>
          
          <div className={styles.filterContainer} ref={filtersRef}>
            <button 
              className={styles.filterButton}
              onClick={() => setShowFilters(!showFilters)}
            >
              <FaFilter className={styles.filterIcon} />
              <span>Filtreler</span>
              {Object.keys(filters).length > 0 && (
                <span className={styles.filterBadge}>
                  {Object.keys(filters).length}
                </span>
              )}
            </button>
            
            {showFilters && (
              <div className={styles.filterDropdown}>
                <div className={styles.filterHeader}>
                  <h3>Filtreler</h3>
                  <button 
                    className={styles.clearFiltersBtn}
                    onClick={clearFilters}
                  >
                    Tümünü Temizle
                  </button>
                </div>
                
                <div className={styles.filterOptions}>
                  {Object.entries(columns).map(([key, col]) => (
                    <div key={key} className={styles.filterOption}>
                      <label>{col.label}</label>
                      <TableFilter 
                        column={key} 
                        type={col.type} 
                        options={col.options} 
                        onFilter={handleFilter}
                        currentValue={filters[key] || ''} 
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {selectedEmployee && (
          <div className={`${styles.actionPanel} ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className={styles.selectedEmployeeInfo}>
              <h3 className={styles.selectedName}>
                {selectedEmployee.eName} {selectedEmployee.eSurname}
              </h3>
              <span className={styles.departmentBadge}>
                {selectedEmployee.departman || 'Departman belirtilmemiş'}
              </span>
            </div>
            
            <div className={styles.assignedSopsInfo}>
              <h4>Atanan SOP'lar</h4>
              <div className={styles.sopsList}>
                {getRequiredSOPs(selectedEmployee.departman).map((sop, index) => (
                  <div key={sop.id} className={styles.sopItem}>
                    <span className={styles.sopName}>{sop.name || '-'}</span>
                    <span className={styles.sopVersion}>
                      {sop.versionNumber ? `v${sop.versionNumber}` : '-'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <section className={`${styles.tableSection} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={`${styles.tableWrapper} ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className={`${styles.tableScrollContainer} ${isDarkMode ? 'dark-mode' : ''}`}>
              {showLeftScroll && (
                <button
                  className={`${styles.scrollButton} ${styles.scrollLeft}`}
                  onClick={() => scrollTable('left')}
                >
                  <FaChevronLeft />
                </button>
              )}
              {showRightScroll && (
                <button
                  className={`${styles.scrollButton} ${styles.scrollRight}`}
                  onClick={() => scrollTable('right')}
                >
                  <FaChevronRight />
                </button>
              )}
              <div
                ref={tableContainerRef}
                className={`${styles.tableContainer} ${isDarkMode ? 'dark-mode' : ''}`}
                onScroll={handleScroll}
                onWheel={(e) => e.stopPropagation()}
              >
                <table className={`${styles.table} ${isDarkMode ? 'dark-mode' : ''}`}>
                  <thead>
                    <tr>
                      <th>Çalışan Adı</th>
                      <th>Proje</th>
                      <th>Departman</th>
                      <th>Gerekli SOP'lar</th>
                      <th>İlerleme</th>
                      <th>Durum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmployees.length > 0 ? (
                      filteredEmployees.map((emp, index) => {
                        const requiredSOPs = getRequiredSOPs(emp.departman);
                        const status = getStatus(index);
                        // Calculate a random progress percentage between 0-100 based on status
                        const progressPercentage = status.text === 'Başarılı' ? 100 : 
                                                  status.text === 'Başarısız' ? Math.floor(Math.random() * 65) : 
                                                  Math.floor(Math.random() * 35) + 65;
                        
                        return requiredSOPs.length > 0 && (
                          <tr 
                            key={emp.id} 
                            onClick={() => handleEmployeeSelect(emp)}
                            className={selectedEmployee && selectedEmployee.id === emp.id ? styles.selectedRow : ''}
                          >
                            <td>{emp.eName && emp.eSurname ? `${emp.eName} ${emp.eSurname}` : '-'}</td>
                            <td>{emp.projects && emp.projects.length > 0 
                                ? emp.projects.map(project => project.titleShort || '-').join(', ')
                                : '-'}</td>
                            <td>{emp.departman || '-'}</td>
                            <td>
                              {requiredSOPs.length > 0 ? (
                                <div className={styles.sopList}>
                                  {requiredSOPs.map(sop => (
                                    <div key={sop.id} className={styles.sopItem}>
                                      <span className={styles.sopName}>{sop.name || '-'}</span>
                                      <span className={styles.sopVersion}>
                                        {sop.versionNumber ? `v${sop.versionNumber}` : '-'}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              ) : '-'}
                            </td>
                            <td>
                              <div className={styles.progressBar}>
                                <div 
                                  className={styles.progressFill} 
                                  style={{ 
                                    width: `${progressPercentage}%`,
                                    backgroundColor: progressPercentage === 100 ? '#38A169' : 
                                                    progressPercentage < 65 ? '#E53E3E' : '#DD6B20'
                                  }}
                                ></div>
                                <span>{progressPercentage === 100 ? 
                                      `${requiredSOPs.length}/${requiredSOPs.length}` : 
                                      `${Math.floor(requiredSOPs.length * progressPercentage / 100)}/${requiredSOPs.length}`}
                                </span>
                              </div>
                            </td>
                            <td>
                              <span 
                                className={styles.statusText}
                                style={{ 
                                  backgroundColor: status.backgroundColor,
                                  color: status.color
                                }}
                              >
                                {status.icon} {status.text}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={6} className={styles.noDataCell}>
                          <div className={styles.noData}>
                            <FaBook className={styles.noDataIcon} />
                            <p>Filtrelere uygun eğitim kaydı bulunamadı</p>
                            {(Object.keys(filters).length > 0 || searchTerm) && (
                              <button className={styles.clearFiltersButton} onClick={clearFilters}>
                                Filtreleri Temizle
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={isDarkMode ? "dark" : "light"}
      />
    </div>
  );
};

export default EducationManagementPage;